import { InferenceStackDiagram } from '../visuals/InferenceStackDiagram';

/**
 * Architecture Diagram Section
 *
 * Shows where Forecaster sits in the inference stack.
 * 1. The Complete Inference Stack (3-layer diagram)
 * 2. Forecaster Controller flow (request → controller → infrastructure)
 */
export const ArchitectureSection = () => {
    return (
        <section className="py-20 md:py-28 px-4 sm:px-6 bg-obsidian-900 relative overflow-hidden">
            {/* Subtle background glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-volt-500/[0.03] rounded-full blur-[120px]" />

            <div className="max-w-5xl mx-auto relative">
                <div className="text-center mb-14">
                    <p className="text-volt-400 text-sm font-medium tracking-wider uppercase mb-3">
                        Architecture
                    </p>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Where Forecaster Sits
                    </h2>
                    <p className="text-gray-400 max-w-2xl mx-auto">
                        A Kubernetes-native controller that sits between your workloads and GPU infrastructure.
                        No sidecar. No proxy. One Helm install.
                    </p>
                </div>

                {/* 1. The Complete Inference Stack */}
                <div className="mb-14">
                    <h3 className="text-xl font-semibold text-white text-center mb-6">
                        The Complete Inference Stack
                    </h3>
                    <InferenceStackDiagram />
                </div>

                {/* 2. Forecaster Controller Flow */}
                <div className="relative">
                    <h3 className="text-xl font-semibold text-white text-center mb-6">
                        Forecaster Controller
                    </h3>

                    <div className="bg-obsidian-800/50 border border-white/[0.06] rounded-2xl p-6 md:p-10">
                        {/* Top: Inference Requests */}
                        <div className="flex justify-center mb-6">
                            <div className="px-6 py-3 bg-white/5 border border-white/10 rounded-xl text-center">
                                <p className="text-sm text-gray-400 mb-1">Incoming</p>
                                <p className="text-white font-semibold">Inference Requests</p>
                                <p className="text-xs text-gray-500 mt-1">Chat, Agentic, RAG, Batch, Realtime</p>
                            </div>
                        </div>

                        {/* Arrow down */}
                        <div className="flex justify-center mb-6">
                            <div className="flex flex-col items-center">
                                <div className="w-px h-8 bg-gradient-to-b from-white/20 to-volt-500/40" />
                                <div className="w-2 h-2 border-r border-b border-volt-500/60 rotate-45 -mt-1" />
                            </div>
                        </div>

                        {/* Forecaster Box */}
                        <div className="border-2 border-volt-500/30 rounded-2xl p-6 mb-6 bg-volt-500/[0.03] relative">
                            <div className="absolute -top-3.5 left-6 px-3 py-0.5 bg-obsidian-900 border border-volt-500/30 rounded-full">
                                <span className="text-volt-400 text-xs font-semibold tracking-wider uppercase">Forecaster Controller</span>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                                <ArchBlock title="Predict" desc="Demand forecasting" detail="Minutes ahead" />
                                <ArchBlock title="Route" desc="Request routing" detail="Cache + cost aware" />
                                <ArchBlock title="Scale" desc="Autoscaling" detail="Predictive, not reactive" />
                                <ArchBlock title="Optimize" desc="Cost + performance" detail="Per-request tracking" />
                            </div>

                            {/* Sub-components row */}
                            <div className="mt-4 pt-4 border-t border-volt-500/10">
                                <div className="flex flex-wrap gap-2 justify-center">
                                    <Chip label="Memory Management" />
                                    <Chip label="Auto-Classification" />
                                    <Chip label="Multi-Cloud" />
                                    <Chip label="Hardware Aware" />
                                    <Chip label="Safety Gates" />
                                    <Chip label="Compliance" />
                                </div>
                            </div>
                        </div>

                        {/* Arrow down */}
                        <div className="flex justify-center mb-6">
                            <div className="flex flex-col items-center">
                                <div className="w-px h-8 bg-gradient-to-b from-volt-500/40 to-white/20" />
                                <div className="w-2 h-2 border-r border-b border-white/30 rotate-45 -mt-1" />
                            </div>
                        </div>

                        {/* Bottom: Infrastructure */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="px-5 py-4 bg-white/[0.03] border border-white/10 rounded-xl">
                                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Inference Engines</p>
                                <div className="flex flex-wrap gap-1.5">
                                    <Tag label="vLLM" />
                                    <Tag label="SGLang" />
                                    <Tag label="TensorRT-LLM" />
                                    <Tag label="Triton" />
                                </div>
                            </div>
                            <div className="px-5 py-4 bg-white/[0.03] border border-white/10 rounded-xl">
                                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">GPU Hardware</p>
                                <div className="flex flex-wrap gap-1.5">
                                    <Tag label="H100" />
                                    <Tag label="A100" />
                                    <Tag label="L40S" />
                                    <Tag label="MI300X" />
                                </div>
                            </div>
                            <div className="px-5 py-4 bg-white/[0.03] border border-white/10 rounded-xl">
                                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Cloud Providers</p>
                                <div className="flex flex-wrap gap-1.5">
                                    <Tag label="AWS" />
                                    <Tag label="GCP" />
                                    <Tag label="Azure" />
                                    <Tag label="RunPod" />
                                    <Tag label="Lambda" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Deployment stats */}
                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                        <div>
                            <p className="text-2xl font-bold text-white">1</p>
                            <p className="text-sm text-gray-400 mt-1">Helm install. One namespace.</p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">0</p>
                            <p className="text-sm text-gray-400 mt-1">Code changes required.</p>
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">&lt;5 min</p>
                            <p className="text-sm text-gray-400 mt-1">To first prediction.</p>
                        </div>
                    </div>
                </div>

                {/* 3. Workload Classification */}
                <div className="mt-20">
                    <h3 className="text-xl font-semibold text-white text-center mb-6">
                        Workload Classification
                    </h3>
                    <p className="text-gray-400 text-center max-w-2xl mx-auto mb-8">
                        Every request is auto-classified and routed to the right GPU with the right SLO.
                        Same model, different workloads — different infrastructure paths.
                    </p>

                    <div className="bg-obsidian-800/50 border border-white/[0.06] rounded-2xl p-6 md:p-8">
                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Online */}
                            <div className="p-5 rounded-xl border border-volt-500/20 bg-volt-500/[0.03]">
                                <div className="text-xs text-volt-400 uppercase tracking-wider font-medium mb-4">Online — Latency Optimized</div>
                                <div className="space-y-2">
                                    {[
                                        { type: 'Chat', slo: 'Low latency' },
                                        { type: 'Agentic', slo: 'Burst-tolerant' },
                                        { type: 'Realtime', slo: 'Ultra-low latency' },
                                        { type: 'Completion', slo: 'Interactive' },
                                    ].map((w) => (
                                        <div key={w.type} className="flex items-center justify-between p-2.5 rounded-lg bg-obsidian-900/60">
                                            <span className="text-sm font-medium text-white">{w.type}</span>
                                            <span className="text-xs text-volt-400 font-mono">{w.slo}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Offline */}
                            <div className="p-5 rounded-xl border border-white/[0.06] bg-white/[0.02]">
                                <div className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-4">Offline — Throughput Optimized</div>
                                <div className="space-y-2">
                                    {[
                                        { type: 'Batch', slo: 'Cost priority' },
                                        { type: 'Moderation', slo: 'High throughput' },
                                        { type: 'Transcription', slo: 'Deferrable' },
                                        { type: 'Corpus prep', slo: 'Off-peak' },
                                    ].map((w) => (
                                        <div key={w.type} className="flex items-center justify-between p-2.5 rounded-lg bg-obsidian-900/60">
                                            <span className="text-sm font-medium text-white">{w.type}</span>
                                            <span className="text-xs text-gray-500 font-mono">{w.slo}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Classification pipeline */}
                        <div className="mt-6 pt-6 border-t border-white/[0.06]">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] text-center">
                                    <p className="text-sm font-semibold text-white mb-1">Classify</p>
                                    <p className="text-xs text-gray-400">Auto-detects workload type from request characteristics</p>
                                </div>
                                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] text-center">
                                    <p className="text-sm font-semibold text-white mb-1">Enforce SLO</p>
                                    <p className="text-xs text-gray-400">Match workload to GPU that can meet its latency or throughput target</p>
                                </div>
                                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] text-center">
                                    <p className="text-sm font-semibold text-white mb-1">Comply</p>
                                    <p className="text-xs text-gray-400">Data sovereignty, SOC2, HIPAA, multi-tenancy isolation</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4. Topology Awareness */}
                <div className="mt-20">
                    <h3 className="text-xl font-semibold text-white text-center mb-6">
                        Topology-Aware Routing
                    </h3>
                    <p className="text-gray-400 text-center max-w-2xl mx-auto mb-8">
                        Forecaster auto-detects how your GPUs are connected and routes workloads
                        to minimize data movement. Not just which GPU — which path.
                    </p>

                    <div className="bg-obsidian-800/50 border border-white/[0.06] rounded-2xl p-6 md:p-8">
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                                <p className="text-sm font-semibold text-white mb-2">GPU-to-GPU Links</p>
                                <p className="text-xs text-gray-400">
                                    Auto-detects high-speed GPU interconnects. Keeps multi-GPU workloads
                                    on the same domain for maximum bandwidth.
                                </p>
                            </div>
                            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                                <p className="text-sm font-semibold text-white mb-2">Cross-Node Fabric</p>
                                <p className="text-xs text-gray-400">
                                    Discovers your network fabric topology. Places workloads to minimize
                                    hop count and maximize throughput between nodes.
                                </p>
                            </div>
                            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                                <p className="text-sm font-semibold text-white mb-2">Session Continuity</p>
                                <p className="text-xs text-gray-400">
                                    Sessions follow users across infrastructure.
                                    No recomputing. No cold starts.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const ArchBlock = ({ title, desc, detail }: { title: string; desc: string; detail: string }) => (
    <div className="p-3 bg-obsidian-900/60 rounded-lg border border-volt-500/10 text-center">
        <p className="text-white font-semibold text-sm">{title}</p>
        <p className="text-gray-400 text-xs mt-0.5">{desc}</p>
        <p className="text-volt-400/70 text-xs mt-1">{detail}</p>
    </div>
);

const Chip = ({ label }: { label: string }) => (
    <span className="px-2.5 py-1 text-xs text-gray-400 bg-white/[0.03] border border-white/[0.06] rounded-full">
        {label}
    </span>
);

const Tag = ({ label }: { label: string }) => (
    <span className="px-2 py-0.5 text-xs text-gray-300 bg-white/5 rounded">
        {label}
    </span>
);
